import { createContext, useContext } from "react";
import CN from "classnames";
import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons/faCheckCircle";
import { faCircle } from "@fortawesome/pro-regular-svg-icons/faCircle";
import { styled } from "@mui/material/styles";
import { useField } from "formik";

import { Button, ButtonList } from "./Button";

interface FormInlineRadioContextShape {
  value: string | boolean | number | null;
  setValue?: (newValue: string | boolean | number | null) => void;
}

const FormRadioContext = createContext<FormInlineRadioContextShape>({
  value: null,
  setValue: undefined,
});

interface FormInlineRadioGroupProps extends FormInlineRadioContextShape {
  children: React.ReactNode;
  className?: string;
}

export const FormInlineRadioGroup = ({ value, setValue, children, className }: FormInlineRadioGroupProps) => (
  <GroupWrapper className={className}>
    <FormRadioContext.Provider value={{ value, setValue }}>{children}</FormRadioContext.Provider>
  </GroupWrapper>
);

export const FormInlineRadioItem = ({
  value,
  children,
  className,
  "data-testid": dataTestid = `radio-${value}`,
  ...propsToRadioButton
}: Omit<React.ComponentProps<typeof RadioButton>, "value"> & {
  value: string | boolean | number | null;
  children: React.ReactNode;
  className?: string;
  "data-testid"?: string;
}) => {
  const { value: currentValue, setValue } = useContext(FormRadioContext);
  const isActive = value === currentValue;

  return (
    <RadioButton
      {...propsToRadioButton}
      className={CN(className, {
        active: isActive,
      })}
      onClick={() => setValue?.(value)}
      prefixFaIcon={isActive ? faCheckCircle : faCircle}
      data-testid={dataTestid}
    >
      {children}
    </RadioButton>
  );
};

const GroupWrapper = styled(ButtonList)`
  margin-top: 0;
`;

const RadioButton = styled(Button)`
  border: 1px solid ${({ theme }) => theme.palette.dream.grayLight};
  background: none;
  display: block;
  flex-grow: 1;

  &:last-child {
    margin-right: 0;
    margin-bottom: 0;
  }

  &:hover {
    background: ${({ theme }) => theme.palette.dream.grayLight};
  }

  &:focus {
    border-width: 1px;
  }

  &.active {
    border-color: ${({ theme }) => theme.palette.primary.main};
    svg {
      opacity: 1;
    }
  }

  svg {
    opacity: 0.25;
  }

  ${({ theme }) => theme.breakpoints.down("sm")} {
    text-align: left;
  }
`;

type FormInlineRadioGroupWithFormikProps = {
  name: string;
  children: React.ReactNode;
  onChange?: (newValue: string | number | boolean | null) => void;
  className?: string;
};

export const FormInlineRadioGroupWithFormik = ({
  children,
  name,
  onChange,
  className,
}: FormInlineRadioGroupWithFormikProps) => {
  const [field, , helpers] = useField(name);

  const handleChange = async (newValue: string | number | boolean | null) => {
    await helpers.setValue(newValue, true);
    await helpers.setTouched(true);
    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <FormInlineRadioGroup value={field.value} setValue={handleChange} className={className}>
      {children}
    </FormInlineRadioGroup>
  );
};
